import * as yup from 'yup';

const parseNumeric = (value: any, originalValue: any): number | undefined => {
  // Handle string input with proper sanitization
  if (typeof originalValue === 'string') {
    const trimmed = originalValue.trim();
    if (trimmed === '') return undefined;

    const parsed = Number(trimmed);
    return Number.isNaN(parsed) ? undefined : parsed;
  }

  // Handle numeric input
  if (typeof originalValue === 'number') {
    return Number.isNaN(originalValue) ? undefined : originalValue;
  }

  // Handle null/undefined
  if (originalValue == null) return undefined;

  // Fallback for transformed value
  return typeof value === 'number' && !Number.isNaN(value) ? value : undefined;
};

export const transferOrderValidationSchema = yup.object().shape({
  sourceLocationId: yup
    .number()
    .nullable()
    .required('Source location is required'),

  destinationLocationId: yup
    .number()
    .nullable()
    .required('Destination location is required')
    .test(
      'different-from-source',
      'Source and destination cannot be the same location',
      function (value) {
        const { sourceLocationId } = this.parent as {
          sourceLocationId: number | null;
        };
        return (
          sourceLocationId == null ||
          value == null ||
          sourceLocationId !== value
        );
      }
    ),

  transferOrderNumber: yup
    .string()
    .nullable()
    .trim()
    .max(50, 'Transfer order number cannot exceed 50 characters'),

  transferReason: yup
    .string()
    .nullable()
    .trim()
    .max(500, 'Transfer reason cannot exceed 500 characters'),

  items: yup
    .array()
    .of(
      yup.object().shape({
        itemId: yup.number().nullable().required('Item is required'),

        quantity: yup
          .number()
          .nullable()
          .transform(parseNumeric)
          .required('Quantity is required')
          .min(1, 'Quantity must be greater than 0')
          .integer('Quantity must be a whole number'),

        accountingClassId: yup
          .number()
          .nullable()
          .required('Accounting class is required'),

        uomId: yup.number().nullable(),

        notes: yup
          .string()
          .nullable()
          .trim()
          .max(1000, 'Notes cannot exceed 1000 characters'),
      })
    )
    .test(
      'unique-items',
      'Cannot add duplicate items to the same transfer order',
      function (items) {
        if (!items || items.length === 0) return true;

        const itemIds = items
          .filter((item) => item?.itemId != null && !item.isDeleted)
          .map((item) => item.itemId as number);

        const uniqueItemIds = new Set(itemIds);
        return itemIds.length === uniqueItemIds.size;
      }
    ),
});

export const transferOrderItemValidationSchema = yup.object().shape({
  itemId: yup.number().nullable().required('Item is required'),

  quantity: yup
    .number()
    .nullable()
    .transform(parseNumeric)
    .required('Quantity is required')
    .min(1, 'Quantity must be greater than 0')
    .integer('Quantity must be a whole number'),

  accountingClassId: yup
    .number()
    .nullable()
    .required('Accounting class is required'),

  uomId: yup.number().nullable(),

  notes: yup
    .string()
    .nullable()
    .trim()
    .max(1000, 'Notes cannot exceed 1000 characters'),
});
